import { Logger, withLogger } from '../../helpers/bi';
import { NewTenantParams, withTranslator, SignupType, isTenantCreation } from '../../helpers/shared';
import * as React from 'react';
import { MkSignup } from './mk-signup/comp';
import { AccountState } from '../comp';
import { MobileLock } from '../mobile-lock/comp';
import { doSignupWithEmail, doSignupRedirect } from '../../helpers/api.client';
import { updateFreeTrialGtmEvent } from '../../helpers/gtm';
import { BIEvent } from '../../helpers/bi/event-type';
import { checkIfMobile, getQueryParamByName } from '../../helpers/utils';
import { UserPhoneNumber } from '@wix/answers-api';
import { TranslateFn } from '@wix/answers-lib';
import { iconsMap } from '../icons/icons';
import { Icon } from '@wix/answers-ui-libby';
import { MkSignupDesigned } from './mk-signup-designed/comp';
import { getMarketingParams } from '../../helpers/marketo/mkto-api-client';

const classNameKey = 'special-signup-container';
const MARKETING_TRAFFIC_SOURCE = 'mk';
const designedSignupContainer = 'special-signup-designed-container';

export type SpecialCreateAccountProps = {
	onFinishSignup: (subdomain: string, email: string, token: string | null, trafficsource?: string) => void;
} & withLogger & withTranslator;

export type SpecialCreateAccountState = {
	signupStep: AccountState;
	userEmail: string;
	userName: string;
	userPassword: string;
	organizationName: string;
	subdomain: string;
	locale: string;
	lockMobile: boolean;
	prefilledPhoneNumber: string;
	phoneNumber?: UserPhoneNumber;
	mkSignupDesign: string;
};

export class SpecialCreateAccount extends React.PureComponent<SpecialCreateAccountProps, SpecialCreateAccountState> {
	logger: Logger = this.props.logger;
	compTranslate: TranslateFn = (key, params) => this.props.t(`marketing-site.signup-form.signup.${key}`, params);
	requiredError = this.compTranslate('errors.required-field');

	captcha: any = null;
	subdomainCheckTimer: any = null;
	subdomainCheckDelay = 500;

	getQuery = (query: string) => getQueryParamByName(query) || '';
	getQueryAndDecode = (query: string) => atob(getQueryParamByName(query) || '');

	state: SpecialCreateAccountState = {
		signupStep: AccountState.SIGNUP,
		userEmail: this.getQueryAndDecode('em'),
		userName: this.getQueryAndDecode('fn'),
		organizationName: this.getQueryAndDecode('or'),
		userPassword: '',
		subdomain: '',
		locale: '',
		lockMobile: false,
		prefilledPhoneNumber: this.getQueryAndDecode('pn'),
		mkSignupDesign: this.getQuery('des_mk')
	};

	onEmailSignupSubmit = (params: NewTenantParams & { recaptchaValue: string }) => {
		const { userEmail, userName, userPassword, organizationName, subdomain, locale, phoneNumber } = params;

		this.setState({
			userEmail,
			userName,
			userPassword,
			organizationName,
			subdomain,
			locale,
			phoneNumber
		}, () => this.signupWithEmail(params.recaptchaValue));
	}

	getUserData = (): NewTenantParams => {
		const { state } = this;

		const maybePhoneNumber = state.phoneNumber ? { phoneNumber: state.phoneNumber } : {};

		return {
			userEmail: state.userEmail,
			userPassword: state.userPassword,
			userName: state.userName,
			subdomain: state.subdomain,
			locale: state.locale,
			organizationName: state.organizationName,
			...maybePhoneNumber
		};
	}

	signupWithEmail = (recaptchaValue: string) => {
		const marketingParams = getMarketingParams();
		const params = { ...this.getUserData(), recaptchaValue };

		doSignupWithEmail(params, marketingParams)
			.then((res) => {
				const tenant = isTenantCreation(res) ? res.tenant : res;
				const token = isTenantCreation(res) ? res.token : null;

				this.logger.logSignupSuccess(BIEvent.SIGNUP_SUCCESS_STEP2, tenant, params, SignupType.EMAIL);
				updateFreeTrialGtmEvent('marketing');

				if (checkIfMobile()) {
					this.setState({lockMobile: true});
				} else {
					this.props.onFinishSignup(params.subdomain, params.userEmail, token, MARKETING_TRAFFIC_SOURCE);
				}
			}, (err) => {
				this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {error: `Server error - ${err.statusText}`, params});
				alert(this.compTranslate('errors.general-error-alert'));
				doSignupRedirect();
			});
	}

	renderComp () {
		const { props, state } = this;
		const { userName, userEmail, organizationName, prefilledPhoneNumber, mkSignupDesign } = state;
		const isMkSignupDesign = !!mkSignupDesign;

		if (state.lockMobile) {
			return <MobileLock t={props.t} />;
		}

		if (isMkSignupDesign) {
			return (
				<div className={designedSignupContainer}>
					<MkSignupDesigned logger={this.logger} onSubmit={this.onEmailSignupSubmit} t={props.t} mkSignupDesign={mkSignupDesign} prefilledUserData={{ userName, userEmail, organizationName, prefilledPhoneNumber }}/>
				</div>
			);
		}

		return (
			<div className={classNameKey}>
				<a className='logo-link' href='/'>
						<Icon className='logo' icon={iconsMap.wixLogo} />
				</a>
				<div className='wrapper'>
					<MkSignup logger={this.logger} onSubmit={this.onEmailSignupSubmit} t={props.t} prefilledUserData={{ userName, userEmail, organizationName, prefilledPhoneNumber }} />
				</div>
			</div>
		);

	}

	render () {
		return this.renderComp();
	}
}
